// this is a part of https://help.getflowbox.com/en/articles/3569385-embedding-a-flow-in-a-react-application

import {
  Component,
  Element,
  Host,
  Listen,
  Prop,
  Watch,
  h,
} from '@stencil/core';
import type {
  FlowboxConfig,
  FlowboxFunction,
  FlowboxTag,
} from '../../helpers/utils/flowbox-types';

import type { CssClassMap } from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-flowbox',
  styleUrl: 'cms-flowbox.scss',
  shadow: true,
})
export class CmsFlowbox {
  private readonly libBaseUrl = 'https://connect.getflowbox.com/flowbox.js';
  private proxyContainer: HTMLDivElement;
  private slotContainer: HTMLElement;
  private flowTags: FlowboxTag[] = [];
  private allTags: string[];

  private flowboxConfig: FlowboxConfig = {
    container: null,
    key: null,
    locale: null,
    tagsOperator: 'any',
  };

  /**
   * set the usercentrics consent status
   */
  @Prop({ reflect: true }) isConsent: boolean;

  /**
   * set the flowbox content key
   */
  @Prop() flowContainer: string;

  /**
   * set the flowbox content key
   */
  @Prop() flowKey: string;

  /**
   * set the locale like "de_DE"
   */
  @Prop() locale: string;

  /**
   * set set the flow tags
   */
  @Prop() tags: FlowboxTag[] | string;

  /**
   * set the type of the flowbox element
   */
  @Prop() flowboxType: 'basic' | 'tags' | 'filter' = 'basic';

  /**
   * set the accessible name
   */
  @Prop() accessibleName: string;

  @Element() hostElement: HTMLElement;

  @Watch('tags')
  private handleTagsChange(newValue: FlowboxTag[] | string) {
    if (!newValue) return;
    this.flowTags =
      typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
  }

  @Listen('cmsCardClick')
  handleCardClick(event: CustomEvent<string>) {
    const clickedTag = event.detail;
    this.updateFlowBox(clickedTag);
  }

  /**
   * Creates and initializes a queue function for Flowbox operations.
   *
   * This function creates a special function that acts as both a queue manager
   * and a function. When called, it stores its arguments in an internal queue
   * for later processing by the Flowbox library.
   */
  private initializeFlowboxQueue(): FlowboxFunction {
    const flowboxQueue = ((...args: any[]) => {
      flowboxQueue.q = flowboxQueue.q || [];
      flowboxQueue.q.push(args);
    }) as FlowboxFunction;
    flowboxQueue.q = [];
    return flowboxQueue;
  }

  /**
   * Creates a new script element for the Flowbox library.
   *   - The specified ID
   *   - Async loading enabled
   *   - Source URL set to the Flowbox library URL
   */
  private createScriptElement(id: string): HTMLScriptElement {
    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = this.libBaseUrl;
    return script;
  }

  /**
   * Loads the Flowbox library and initializes its queue system.
   *
   * This function handles the dynamic loading of the Flowbox JavaScript library.
   * It implements a queue-based initialization pattern to ensure that any
   * Flowbox commands called before the library loads are properly queued
   * and executed once loading is complete.
   */
  private loadFlowbox(): Promise<boolean> {
    return new Promise((resolve) => {
      const scriptId = 'flowbox-js-embed';

      // Return early if flowbox is already loaded
      if (document.getElementById(scriptId)) {
        return resolve(true);
      }

      // Initialize flowbox queue if it doesn't exist
      if (!window.flowbox) {
        window.flowbox = this.initializeFlowboxQueue();
      }

      // Insert script into DOM
      const script = this.createScriptElement(scriptId);
      const lastScript = document.scripts[document.scripts.length - 1];
      lastScript.parentNode.insertBefore(script, lastScript);

      resolve(true);
    });
  }

  private updateFlowBox(tags: string) {
    this.flowboxConfig = {
      ...this.flowboxConfig,
      tags: tags === 'all' ? this.allTags : [tags],
    };
    window.flowbox('update', this.flowboxConfig);
  }

  private setupDOMElements() {
    // Create a slot container that will be exposed outside shadow DOM
    this.slotContainer = document.createElement('div');
    this.slotContainer.setAttribute('slot', 'flowbox-content');

    // Create the actual container for Flowbox
    this.proxyContainer = document.createElement('div');
    this.proxyContainer.id = this.flowContainer;

    // Add Flowbox container inside the slot container
    this.slotContainer.appendChild(this.proxyContainer);

    // Add the slot container to the light DOM
    this.hostElement.appendChild(this.slotContainer);
  }

  private initializeFlowboxConfig() {
    return {
      ...this.flowboxConfig,
      tags: this.tags ? this.allTags : null,
      container: `#${this.flowContainer}`,
      key: this.flowKey,
      locale: this.locale,
    };
  }

  private wrapperClassMap(): CssClassMap {
    return {
      'flowbox-wrapper': true,
      [this.flowboxType]: true,
    };
  }

  async connectedCallback() {
    if (this.tags) {
      this.handleTagsChange(this.tags);
      this.allTags = this.flowTags?.map((tag) => tag.value) || [];
    }

    this.setupDOMElements();
    this.flowboxConfig = this.initializeFlowboxConfig();

    if (this.isConsent) {
      this.loadFlowbox().then(() => {
        window.flowbox('init', this.flowboxConfig);
      });
    }
  }

  async componentWillUpdate() {
    if (this.isConsent) {
      this.loadFlowbox().then(() => {
        window.flowbox('init', this.flowboxConfig);
      });
    }
  }

  render() {
    return (
      <Host>
        {this.flowboxType === 'filter' && (
          <div class='button-wrapper'>
            <cms-card cardType='button' cardContent={this.tags} />
          </div>
        )}
        <div class={'flowbox-inner'}>
          {this.isConsent ? (
            <div
              class={this.wrapperClassMap()}
              aria-label={this.accessibleName}
            >
              <slot name='flowbox-content' />
            </div>
          ) : (
            <div class='consent-container'>
              <slot name='hdp-consent' />
            </div>
          )}
        </div>
      </Host>
    );
  }
}
